import { useEffect, useRef, useState } from 'react';
import type { BuildingType, Command } from '@/game/sim';
import { type LostCity, ownCities, updateLostCities } from '@/game/view/cities';
import { labelOrDefault, nameOf } from '@/game/view/names';
import { isWorker } from '@/game/view/workerIntent';
import { useSimFrame } from '../app/simFrame';
import { InlineRename } from '../InlineRename';
import {
  cancelRename,
  commitRename,
  startRename,
  type UiStore,
  useUi,
  WORKERS_MIME,
} from '../ui/store';

/**
 * The Cities chip row (in the top bar, after the resources): a chip per own
 * completed city in stable City N order (City 1 first, later cities added
 * after it, fixed widths, so no chip moves as cities are added or selected). A chip's name selects the city (while placing, places
 * from it instead) and its ⌖ goes there; a fallen city's chip stays a few
 * seconds, struck through, before it goes. Hidden once the player is out.
 * Dragging the dock's workers (`ui.draggingWorkers`) over a chip and dropping
 * them re-homes them to that city (`setHome`); every chip says so meanwhile.
 * A city the player named shows that name; double-click a chip (or F2 on the
 * selected city) renames it.
 */
export function CityChips({
  activeCityId,
  placementMode,
  onSelectCity,
  onPanToCity,
  ui,
  dispatch,
}: {
  /** The highlighted chip: the city placing from, else the selected one. */
  activeCityId: string | null;
  placementMode: BuildingType | null;
  onSelectCity: (id: string) => void;
  onPanToCity: (id: string) => void;
  ui: UiStore;
  dispatch: (c: Command) => void;
}) {
  const { view: state, playerId, rules, cityNumbers } = useSimFrame();
  const dragging = useUi(ui, (s) => s.draggingWorkers);
  const names = useUi(ui, (s) => s.names);
  const renaming = useUi(ui, (s) => s.renaming);
  const [dropOn, setDropOn] = useState<string | null>(null);
  const eliminated = state.players[playerId]?.eliminated ?? false;
  const completedCities = ownCities(state, rules, playerId).sort(
    (a, b) => (cityNumbers.get(a.id) ?? 0) - (cityNumbers.get(b.id) ?? 0)
  );

  const [lostCities, setLostCities] = useState<LostCity[]>([]);
  const prevCityIdsRef = useRef<string[]>([]);
  const cityIdsKey = completedCities.map((c) => c.id).join(',');
  const elapsed = state.elapsed;
  useEffect(() => {
    const ids = cityIdsKey ? cityIdsKey.split(',') : [];
    setLostCities((lost) =>
      updateLostCities(lost, prevCityIdsRef.current, new Set(ids), cityNumbers, elapsed)
    );
    prevCityIdsRef.current = ids;
  }, [cityIdsKey, cityNumbers, elapsed]);
  const cityChips = [
    ...completedCities.map((city) => ({
      id: city.id,
      number: cityNumbers.get(city.id) ?? 0,
      lost: false,
    })),
    ...lostCities.map((l) => ({ id: l.id, number: l.number, lost: true })),
  ].sort((a, b) => a.number - b.number);

  const label = (chip: { id: string; number: number }) =>
    labelOrDefault(names, chip.id, `City ${chip.number}`);

  if (cityChips.length === 0 || eliminated) return null;
  // While placing, a chip moves the placement to that city's territory.
  return (
    <div className="flex items-center gap-1 text-xs">
      <span className="mr-1 text-fg-muted">Cities</span>
      <span className="flex gap-1">
        {cityChips.map((chip) =>
          chip.lost ? (
            <span
              key={chip.id}
              title="This city fell"
              className="chip w-20 justify-center bg-danger-surface tabular-nums text-danger line-through"
            >
              <span className="truncate px-1">{label(chip)}</span>
            </span>
          ) : (
            // biome-ignore lint/a11y/noStaticElementInteractions: a drop target only; the buttons inside are the chip's controls
            <span
              key={chip.id}
              onDragOver={(e) => {
                if (!e.dataTransfer.types.includes(WORKERS_MIME)) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }}
              onDragEnter={() => dragging && setDropOn(chip.id)}
              onDragLeave={(e) => {
                // Moving onto the chip's own buttons isn't leaving it.
                if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                setDropOn((id) => (id === chip.id ? null : id));
              }}
              onDrop={(e) => {
                const ids = e.dataTransfer.getData(WORKERS_MIME);
                if (!ids) return;
                e.preventDefault();
                // Only own workers, whatever the payload says.
                const unitIds = ids
                  .split(',')
                  .filter((id) => state.units[id] && isWorker(rules, state.units[id], playerId));
                if (unitIds.length > 0) dispatch({ type: 'setHome', unitIds, cityId: chip.id });
                setDropOn(null);
                ui.set({ draggingWorkers: null });
              }}
              className={`chip relative w-20 justify-between gap-0 p-0 tabular-nums transition ${chip.id === activeCityId ? 'chip-selected' : 'text-fg-muted'} ${dragging ? `ring-1 ${dropOn === chip.id ? 'ring-accent' : 'ring-accent/40'}` : ''}`}
            >
              {dragging && (
                <span className="pointer-events-none absolute left-1/2 top-full z-10 mt-1 -translate-x-1/2 whitespace-nowrap rounded-full bg-surface-solid px-1.5 text-[10px] text-accent">
                  drop to re-home
                </span>
              )}
              {renaming?.site === 'chip' && renaming.id === chip.id ? (
                <InlineRename
                  value={nameOf(names, chip.id) ?? ''}
                  label={`Rename ${label(chip)}`}
                  onCommit={(name) => commitRename(ui, chip.id, name)}
                  onCancel={() => cancelRename(ui)}
                  className="my-0.5 ml-1 w-full flex-1"
                />
              ) : (
                <button
                  type="button"
                  title={
                    placementMode === null
                      ? 'Select this city (double-click or F2 to rename)'
                      : `Place the ${rules.buildings[placementMode]?.name ?? placementMode} in this city's territory`
                  }
                  aria-pressed={chip.id === activeCityId}
                  onClick={() => onSelectCity(chip.id)}
                  onDoubleClick={() => startRename(ui, chip.id, 'chip')}
                  className={`min-w-0 flex-1 truncate rounded-full py-0.5 pl-2 text-left ${chip.id === activeCityId ? '' : 'hover:text-fg'}`}
                >
                  {label(chip)}
                </button>
              )}
              <button
                type="button"
                aria-label={`Go to ${label(chip)}`}
                title="Go there"
                onClick={() => onPanToCity(chip.id)}
                className={`rounded-full px-1.5 py-0.5 ${chip.id === activeCityId ? '' : 'hover:text-fg'}`}
              >
                ⌖
              </button>
            </span>
          )
        )}
      </span>
    </div>
  );
}
